import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const { t } = useLang();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | success | pending | error
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setStatus("pending"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = {
    checking: { icon: Loader2, spin: true, title: t("payment_pending"), sub: "" },
    pending: { icon: Loader2, spin: true, title: t("payment_pending"), sub: "" },
    success: { icon: CheckCircle2, spin: false, title: t("payment_success"), sub: t("payment_success_sub") },
    error: { icon: XCircle, spin: false, title: t("payment_error"), sub: "" },
  }[status];

  const Icon = config.icon;

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4 fade-up" data-testid="checkout-success-page">
      <Icon className={`w-16 h-16 mb-6 ${status === "success" ? "text-[#166534]" : status === "error" ? "text-[#991B1B]" : "text-[#C5A880]"} ${config.spin ? "animate-spin" : ""}`} strokeWidth={1} />
      <h1 data-testid="payment-status-title" className="font-serif-display text-4xl text-[#1A1F2C] mb-3">{config.title}</h1>
      {config.sub && <p className="text-[#6B7280] max-w-md mb-8">{config.sub}</p>}
      {(status === "success" || status === "error") && (
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-button"
          className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase px-10 py-4 hover:bg-[#C5A880] transition-colors duration-300"
        >
          {t("back_home")}
        </button>
      )}
    </div>
  );
}
